import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  updateDoc, 
  doc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaCheck, 
  FaTimes, 
  FaClock, 
  FaCalendar,
  FaDownload,
  FaSpinner,
  FaBell,
  FaExclamationTriangle,
  FaEnvelope,
  FaFileAlt,
  FaUserCheck,
  FaUserSlash,
  FaThumbsDown
} from 'react-icons/fa';
import { 
  updateApplicationStatusWithTimeout, 
  APPLICATION_STATUSES,
  getApplicationWithTimeoutInfo,
  EmployerAcceptedApplicationView
} from '../../components/timeoutSystem';
import { emailNotifications } from '../../components/notifications/EmailNotificationComponent';

const ManageApplications = () => {
  const { user } = EAuthData();
  const [applications, setApplications] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    interview: 0,
    studentAccepted: 0,
    studentDeclined: 0,
    awaitingResponse: 0
  });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userd?.uid) return;

      try {
        setLoading(true);
        
        // Fetch positions
        const positionsRef = collection(db, 'positions');
        const positionsQuery = query(
          positionsRef,
          where('employer_id', '==', user.userd.uid)
        );
        const positionsSnapshot = await getDocs(positionsQuery);
        const positionsData = positionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPositions(positionsData);

        // Fetch applications
        const applicationsRef = collection(db, 'applications');
        const applicationsQuery = query(
          applicationsRef,
          where('employerId', '==', user.userd.uid),
          orderBy('appliedAt', 'desc')
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicationsData = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Add timeout info to applications
        const applicationsWithTimeout = applicationsData.map(app => 
          getApplicationWithTimeoutInfo(app)
        );
        setApplications(applicationsWithTimeout);

        // Calculate enhanced statistics
        const stats = {
          total: applicationsData.length,
          pending: applicationsData.filter(app => app.status === APPLICATION_STATUSES.PENDING).length,
          accepted: applicationsData.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length,
          rejected: applicationsData.filter(app => app.status === APPLICATION_STATUSES.REJECTED).length,
          interview: applicationsData.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW).length,
          studentAccepted: applicationsData.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length,
          studentDeclined: applicationsData.filter(app => app.status === APPLICATION_STATUSES.STUDENT_DECLINED).length,
          awaitingResponse: applicationsData.filter(app => 
            app.status === APPLICATION_STATUSES.ACCEPTED && app.timeoutEnabled
          ).length
        };
        setStats(stats);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Enhanced update application status with timeout system
  const updateApplicationStatus = async (application, newStatus) => {
    setUpdating(true);
    try {
      // Use the timeout system function instead of direct update
      await updateApplicationStatusWithTimeout(
        application.id, 
        newStatus, 
        user.userd.uid
      );

      // Send email notification when accepting or rejecting
      if ((newStatus === 'accepted' || newStatus === 'rejected') && application) {
        try {
          // Get student data
          const studentData = {
            email: application.studentEmail,
            name: application.studentName
          };

          // Get position data
          const positionData = {
            title: application.positionTitle,
          };

          // Get company data from current user profile
          const companyData = {
            companyName: user?.profile?.companyName
          };

          await emailNotifications.notifyStudentApplication(
            studentData,
            positionData,
            companyData,
            newStatus
          );
          
          console.log(`✅ Email notification sent to ${studentData.name}`);
        } catch (emailError) {
          console.error('❌ Error sending email notification:', emailError);
          // Don't fail the entire operation if email fails
        }
      }

      // Update local state with timeout info
      setApplications(prev => 
        prev.map(app => 
          app.id === application.id 
            ? getApplicationWithTimeoutInfo({
                ...app, 
                status: newStatus, 
                updatedAt: new Date().toISOString(),
                // Add timeout fields if accepting
                ...(newStatus === APPLICATION_STATUSES.ACCEPTED && {
                  acceptedAt: new Date().toISOString(),
                  studentResponseDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
                  timeoutEnabled: true
                })
              })
            : app
        )
      );

      // Update stats
      setStats(prev => {
        const updatedStats = { ...prev };
        const oldApp = applications.find(app => app.id === application.id);
        
        // Decrement old status
        if (oldApp?.status === APPLICATION_STATUSES.PENDING) updatedStats.pending--;
        if (oldApp?.status === APPLICATION_STATUSES.ACCEPTED) updatedStats.accepted--;
        if (oldApp?.status === APPLICATION_STATUSES.REJECTED) updatedStats.rejected--;
        if (oldApp?.status === APPLICATION_STATUSES.INTERVIEW) updatedStats.interview--;
        if (oldApp?.status === APPLICATION_STATUSES.STUDENT_ACCEPTED) updatedStats.studentAccepted--;
        if (oldApp?.status === APPLICATION_STATUSES.STUDENT_DECLINED) updatedStats.studentDeclined--;
        
        // Increment new status
        if (newStatus === APPLICATION_STATUSES.PENDING) updatedStats.pending++;
        if (newStatus === APPLICATION_STATUSES.ACCEPTED) {
          updatedStats.accepted++;
          updatedStats.awaitingResponse++;
        }
        if (newStatus === APPLICATION_STATUSES.REJECTED) updatedStats.rejected++;
        if (newStatus === APPLICATION_STATUSES.INTERVIEW) updatedStats.interview++;
        if (newStatus === APPLICATION_STATUSES.STUDENT_ACCEPTED) updatedStats.studentAccepted++;
        if (newStatus === APPLICATION_STATUSES.STUDENT_DECLINED) updatedStats.studentDeclined++;
        
        return updatedStats;
      });

    } catch (error) {
      console.error('Error updating application:', error);
      alert('Error updating application. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  // Filter and sort applications
  const filteredAndSortedApplications = applications
    .filter(app => {
      const matchesFilter = filter === 'all' || app.status === filter;
      const matchesSearch = searchTerm === '' || 
        app.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.positionTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.studentEmail?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.appliedAt) - new Date(a.appliedAt);
        case 'name':
          return a.studentName?.localeCompare(b.studentName);
        case 'position':
          return a.positionTitle?.localeCompare(b.positionTitle);
        case 'status':
          return a.status?.localeCompare(b.status);
        case 'deadline':
          // Sort by deadline for accepted applications
          if (a.status === APPLICATION_STATUSES.ACCEPTED && b.status === APPLICATION_STATUSES.ACCEPTED) {
            const aDeadline = a.timeoutInfo?.deadline ? new Date(a.timeoutInfo.deadline) : new Date('9999-12-31');
            const bDeadline = b.timeoutInfo?.deadline ? new Date(b.timeoutInfo.deadline) : new Date('9999-12-31');
            return aDeadline - bDeadline;
          }
          return 0;
        default:
          return 0;
      }
    });

  const getStatusColor = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case APPLICATION_STATUSES.ACCEPTED: return 'bg-blue-100 text-blue-800 border-blue-200';
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return 'bg-green-100 text-green-800 border-green-200';
      case APPLICATION_STATUSES.STUDENT_DECLINED: return 'bg-orange-100 text-orange-800 border-orange-200';
      case APPLICATION_STATUSES.REJECTED: return 'bg-red-100 text-red-800 border-red-200';
      case APPLICATION_STATUSES.INTERVIEW: return 'bg-purple-100 text-purple-800 border-purple-200';
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return <FaClock className="mr-1" />;
      case APPLICATION_STATUSES.ACCEPTED: return <FaBell className="mr-1" />;
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return <FaCheck className="mr-1" />;
      case APPLICATION_STATUSES.STUDENT_DECLINED: return <FaTimes className="mr-1" />;
      case APPLICATION_STATUSES.REJECTED: return <FaTimes className="mr-1" />;
      case APPLICATION_STATUSES.INTERVIEW: return <FaCalendar className="mr-1" />;
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return <FaExclamationTriangle className="mr-1" />;
      default: return <FaClock className="mr-1" />;
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return 'Pending';
      case APPLICATION_STATUSES.ACCEPTED: return 'Awaiting Response';
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return 'Accepted by Student';
      case APPLICATION_STATUSES.STUDENT_DECLINED: return 'Declined by Student';
      case APPLICATION_STATUSES.REJECTED: return 'Rejected';
      case APPLICATION_STATUSES.INTERVIEW: return 'Interview';
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return 'Auto-Declined';
      default: return status;
    }
  };

  // Enhanced Application Card Component
  const ApplicationCard = ({ application }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all duration-200">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-1">{application.studentName}</h3>
          <p className="text-sm text-gray-600 mb-1">{application.studentEmail}</p>
          <p className="text-sm font-medium text-gray-700">{application.positionTitle}</p>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(application.status)}`}>
          {getStatusIcon(application.status)}
          {getStatusDisplayName(application.status)}
        </div>
      </div>

      {/* Timeout Information for Accepted Applications */}
      {application.status === APPLICATION_STATUSES.ACCEPTED && application.timeoutInfo && (
        <EmployerAcceptedApplicationView application={application} />
      )}

      <div className="text-xs text-gray-500 mb-3">
        Applied: {new Date(application.appliedAt).toLocaleDateString()}
        {application.acceptedAt && (
          <span className="ml-3">
            Accepted: {new Date(application.acceptedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={() => {
            setSelectedApplication(application);
            setShowApplicationModal(true);
          }}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          <FaEye className="mr-1" />
          Review Application
        </button>

        {application.status === APPLICATION_STATUSES.PENDING && (
          <div className="flex space-x-2">
            <button
              onClick={() => updateApplicationStatus(application, APPLICATION_STATUSES.INTERVIEW)}
              disabled={updating}
              className="bg-purple-500 text-white px-3 py-1 rounded text-xs hover:bg-purple-600 disabled:opacity-50"
            >
              Interview
            </button>
            <button
              onClick={() => updateApplicationStatus(application, APPLICATION_STATUSES.ACCEPTED)}
              disabled={updating}
              className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600 disabled:opacity-50"
            >
              Accept
            </button>
            <button
              onClick={() => updateApplicationStatus(application, APPLICATION_STATUSES.REJECTED)}
              disabled={updating}
              className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Application Details Modal Component
  const ApplicationModal = () => {
    if (!selectedApplication) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Application Details</h2>
              <button
                onClick={() => setShowApplicationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Student Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Student Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p><strong>Name:</strong> {selectedApplication.studentName}</p>
                  <p><strong>Email:</strong> {selectedApplication.studentEmail}</p>
                  <p><strong>Student ID:</strong> {selectedApplication.studentInternID || 'N/A'}</p>
                </div>
              </div>

              {/* Position Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Position Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p><strong>Position:</strong> {selectedApplication.positionTitle}</p>
                  <p><strong>Applied Date:</strong> {new Date(selectedApplication.appliedAt).toLocaleDateString()}</p>
                  {selectedApplication.acceptedAt && (
                    <p><strong>Accepted Date:</strong> {new Date(selectedApplication.acceptedAt).toLocaleDateString()}</p>
                  )}
                  <p><strong>Status:</strong> 
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedApplication.status)}`}>
                      {getStatusIcon(selectedApplication.status)}
                      {getStatusDisplayName(selectedApplication.status)}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Timeout Information for Accepted Applications */}
            {selectedApplication.status === APPLICATION_STATUSES.ACCEPTED && selectedApplication.timeoutInfo && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Response Status</h3>
                <EmployerAcceptedApplicationView application={selectedApplication} />
              </div>
            )}

            {/* Application Content */}
            <div className="space-y-6 mb-6">
              {selectedApplication.coverLetter && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-800">Cover Letter</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedApplication.coverLetter}</p>
                </div>
              )}
              
              {selectedApplication.whyInterested && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-800">Why Interested</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedApplication.whyInterested}</p>
                </div>
              )}
              
              {selectedApplication.relevantExperience && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-800">Relevant Experience</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedApplication.relevantExperience}</p>
                </div>
              )}
              
              {selectedApplication.availability && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-800">Availability</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedApplication.availability}</p>
                </div>
              )}
              
              {selectedApplication.additionalInfo && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-800">Additional Information</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedApplication.additionalInfo}</p>
                </div>
              )}
            </div>

            {/* Student Response Information */}
            {(selectedApplication.status === APPLICATION_STATUSES.STUDENT_ACCEPTED || 
              selectedApplication.status === APPLICATION_STATUSES.STUDENT_DECLINED) && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Student Response</h3>
                <div className={`p-4 rounded-lg ${
                  selectedApplication.status === APPLICATION_STATUSES.STUDENT_ACCEPTED 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-orange-50 border border-orange-200'
                }`}>
                  <div className="flex items-center mb-2">
                    {selectedApplication.status === APPLICATION_STATUSES.STUDENT_ACCEPTED ? (
                      <FaCheck className="text-green-600 mr-2" />
                    ) : (
                      <FaTimes className="text-orange-600 mr-2" />
                    )}
                    <span className="font-semibold">
                      Student {selectedApplication.status === APPLICATION_STATUSES.STUDENT_ACCEPTED ? 'Accepted' : 'Declined'} Offer
                    </span>
                  </div>
                  {selectedApplication.studentResponseAt && (
                    <p className="text-sm text-gray-600 mb-2">
                      Responded: {new Date(selectedApplication.studentResponseAt).toLocaleDateString()}
                    </p>
                  )}
                  {selectedApplication.declineReason && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Reason for declining:</p>
                      <p className="text-sm text-gray-600 italic">"{selectedApplication.declineReason}"</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApplicationModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              
              {/* Buttons for Pending Applications */}
              {selectedApplication.status === APPLICATION_STATUSES.PENDING && (
                <>
                  <button
                    onClick={() => {
                      updateApplicationStatus(selectedApplication, APPLICATION_STATUSES.INTERVIEW);
                      setShowApplicationModal(false);
                    }}
                    disabled={updating}
                    className="flex items-center bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
                  >
                    {updating ? <FaSpinner className="animate-spin mr-2" /> : <FaCalendar className="mr-2" />}
                    Schedule Interview
                  </button>
                  <button
                    onClick={() => {
                      updateApplicationStatus(selectedApplication, APPLICATION_STATUSES.ACCEPTED);
                      setShowApplicationModal(false);
                    }}
                    disabled={updating}
                    className="flex items-center bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50"
                  >
                    {updating ? <FaSpinner className="animate-spin mr-2" /> : <FaCheck className="mr-2" />}
                    Accept Application
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reject this application? This action cannot be undone.')) {
                        updateApplicationStatus(selectedApplication.id, APPLICATION_STATUSES.REJECTED);
                        setShowApplicationModal(false);
                      }
                    }}
                    disabled={updating}
                    className="flex items-center bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50"
                  >
                    {updating ? <FaSpinner className="animate-spin mr-2" /> : <FaTimes className="mr-2" />}
                    Reject Application
                  </button>
                </>
              )}
              
              {/* Buttons for Interview Status */}
              {selectedApplication.status === APPLICATION_STATUSES.INTERVIEW && (
                <>
                  <button
                    onClick={() => {
                      updateApplicationStatus(selectedApplication, APPLICATION_STATUSES.ACCEPTED);
                      setShowApplicationModal(false);
                    }}
                    disabled={updating}
                    className="flex items-center bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50"
                  >
                    {updating ? <FaSpinner className="animate-spin mr-2" /> : <FaCheck className="mr-2" />}
                    Accept After Interview
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reject this application? This action cannot be undone.')) {
                        updateApplicationStatus(selectedApplication, APPLICATION_STATUSES.REJECTED);
                        setShowApplicationModal(false);
                      }
                    }}
                    disabled={updating}
                    className="flex items-center bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50"
                  >
                    {updating ? <FaSpinner className="animate-spin mr-2" /> : <FaTimes className="mr-2" />}
                    Reject After Interview
                  </button>
                </>
              )}
              
              {/* Email Button - Always Available */}
              <button
                onClick={() => window.open(`mailto:${selectedApplication.studentEmail}`, '_blank')}
                className="flex items-center bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700"
              >
                <FaEnvelope className="mr-2" />
                Email Student
              </button>
            </div>

            {/* Contact Information */}
            {(selectedApplication.status === APPLICATION_STATUSES.STUDENT_ACCEPTED || 
              selectedApplication.status === APPLICATION_STATUSES.INTERVIEW) && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Next Steps</h3>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-blue-800 mb-2">
                    <strong>Contact the student:</strong> {selectedApplication.studentEmail}
                  </p>
                  {selectedApplication.status === APPLICATION_STATUSES.STUDENT_ACCEPTED && (
                    <p className="text-blue-700 text-sm">
                      🎉 The student has accepted your offer! Reach out to discuss onboarding, start dates, and next steps.
                    </p>
                  )}
                  {selectedApplication.status === APPLICATION_STATUSES.INTERVIEW && (
                    <p className="text-blue-700 text-sm">
                      📅 Schedule an interview with this candidate to learn more about their qualifications.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 space-y-4 md:space-y-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Manage Applications</h1>
            <p className="text-gray-600">Review and manage internship applications with automated response tracking</p>
          </div>
        </div>

        {/* Enhanced Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <FaFileAlt className="h-6 md:h-8 w-6 md:w-8 text-gray-900" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Pending</h3>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <FaClock className="h-6 md:h-8 w-6 md:w-8 text-yellow-600" />
            </div>
          </div> 
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Awaiting Response</h3>
              <p className="text-2xl font-bold text-blue-600">{stats.awaitingResponse}</p>
              </div>
              <FaBell className="h-6 md:h-8 w-6 md:w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Confirmed</h3>
              <p className="text-2xl font-bold text-green-600">{stats.studentAccepted}</p>
              </div>
              <FaUserCheck className="h-6 md:h-8 w-6 md:w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Student Declined</h3>
              <p className="text-2xl font-bold text-orange-900">{stats.studentDeclined}</p>
              </div>
              <FaUserSlash className="h-6 md:h-8 w-6 md:w-8 text-orange-900" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Interview</h3>
              <p className="text-2xl font-bold text-purple-600">{stats.interview}</p>
              </div>
              <FaCalendar className="h-6 md:h-8 w-6 md:w-8 text-purple-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
              <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Rejected</h3>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <FaThumbsDown className="h-6 md:h-8 w-6 md:w-8 text-red-600" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value={APPLICATION_STATUSES.PENDING}>Pending</option>
                <option value={APPLICATION_STATUSES.ACCEPTED}>Awaiting Response</option>
                <option value={APPLICATION_STATUSES.STUDENT_ACCEPTED}>Student Accepted</option>
                <option value={APPLICATION_STATUSES.STUDENT_DECLINED}>Student Declined</option>
                <option value={APPLICATION_STATUSES.INTERVIEW}>Interview</option>
                <option value={APPLICATION_STATUSES.REJECTED}>Rejected</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="position">Sort by Position</option>
                <option value="status">Sort by Status</option>
                <option value="deadline">Sort by Deadline</option>
              </select>
            </div>
          </div>
        </div>

        {/* Applications Grid */}
        {filteredAndSortedApplications.length === 0 ? (
          <div className="bg-white p-12 rounded-lg shadow-sm border text-center">
            <FaClock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
            <p className="text-gray-600">
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'Applications will appear here once students start applying to your positions.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedApplications.map((application) => (
              <ApplicationCard key={application.id} application={application} />
            ))}
          </div>
        )}

        {/* Application Details Modal */}
        {showApplicationModal && <ApplicationModal />}

        {/* Loading Overlay */}
        {updating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center">
              <FaSpinner className="animate-spin mr-3 text-orange-500" />
              <span>Updating application...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageApplications;